'use client'

import { useMemo } from 'react'

import { useSessionFromContext } from '../contexts/SessionContext'
import { useProfileUpdates } from './useProfileUpdates'

/**
 * Current user profile derived from session and profile updates.
 * Reads session from SessionContext (set by SessionProvider at app root) to avoid
 * "Cannot read properties of null (reading 'useRef')" when better-auth runs in a different React instance.
 */
export type CurrentUserProfile = {
  id: string
  name: string
  email: string
  displayName?: string
  avatarUrl: string | null
  role: string
  profileCompleted?: boolean
}

type UseCurrentUserProfileResult = {
  profile: CurrentUserProfile | null
  isLoading: boolean
  error: string | null
}

export function useCurrentUserProfile(): UseCurrentUserProfileResult {
  const sessionContext = useSessionFromContext()
  const session = sessionContext?.data ?? null
  const isPending = sessionContext?.isPending ?? true
  const status = isPending ? 'loading' : (session ? 'authenticated' : 'unauthenticated')
  const {
    profileData,
    isLoading: isProfileLoading,
    error: profileError,
  } = useProfileUpdates()

  const profile = useMemo<CurrentUserProfile | null>(() => {
    if (!session?.user) return null

    const baseName =
      session.user.name !== undefined && session.user.name !== null ? session.user.name : ''
    const baseEmail =
      session.user.email !== undefined && session.user.email !== null ? session.user.email : ''
    const baseRole =
      typeof (session.user as { role?: string | null }).role === 'string'
        ? ((session.user as { role?: string | null }).role as string)
        : 'USER'

    const displayName =
      profileData?.displayName && profileData.displayName.trim().length > 0
        ? profileData.displayName
        : baseName

    const name =
      (profileData?.name && profileData.name.trim().length > 0
        ? profileData.name
        : baseName) || baseEmail

    const email = profileData?.email ?? baseEmail

    const avatarUrl =
      profileData?.avatar && profileData.avatar.trim().length > 0
        ? profileData.avatar
        : (session.user as { image?: string | null }).image ?? null

    if (!email && !name) {
      return null
    }

    const sessionUserId = (session.user as { id?: string }).id
    if (!sessionUserId) {
      throw new Error('[useCurrentUserProfile] session.user.id is missing — auth session is malformed')
    }
    const userId = profileData?.id ?? sessionUserId
    return {
      id: userId,
      name,
      email,
      displayName: displayName || undefined,
      avatarUrl,
      role: baseRole,
      profileCompleted: profileData?.profileCompleted,
    }
  }, [session, profileData])

  const isLoading = status === 'loading' || isProfileLoading
  const error = profileError

  return { profile, isLoading, error }
}


